import { useContext, useMemo, useState } from "react"
import { Line } from "react-chartjs-2";
import { PageLoading } from "../../components/pageLoading";
import { DashboardContent, DashboardHeader, DashboardTitle, InnerContainer, InnerContainerRow, InnerContainerTitle, InnerContainerTitleS, InputCheckbox } from "../../components/dashboard";
import { BarElement, FlexBar } from "../../components/bar";
import { AuthenticationContext } from "../../components/authentication";
import { Notice } from "../../components/containers/notice";
import { TimestampToDateTime } from "../../components/timestampToDateTime";

import { useCurrentEvent } from "../../hooks/events/useCurrentEvent";
import { useEventTickets } from "../../hooks/tickets/useEventTickets";
import { useEventTicketTypeMappings } from "../../hooks/tickets/useEventTicketTypeMappings";
import { useActiveStoreSessions } from "../../hooks/storeSessions/useActiveStoreSessions";
import { useTicketSaleData } from "../../hooks/stats/useTicketSaleData";
import { useBrand } from "../../contexts/brand";
import { hasAnyBrandPermission } from "../../utils/roles";

const SECONDS_PER_DAY = 24 * 60 * 60;

// Tickets sold up to and including the given day of an event's ticket sale
const getSoldByDay = (salesEvent, day) => salesEvent.days
    .filter((entry) => entry.idx <= day)
    .reduce((sum, entry) => sum + entry.count, 0);

const PREDICTION_COLORS =["rgb(54, 162, 235)", "rgb(255, 99, 132)", "rgb(75, 192, 192)", "rgb(255, 159, 64)", "rgb(153, 102, 255)"];

const predictionChartOptions = {
    responsive: true,
    interaction: {
        mode: "index",
        intersect: false,
    },
    elements: {
        point: {
            radius: 0,
        },
    },
    plugins: {
        legend: {
            position: "top",
        },
        title: {
            display: true,
            text: "Forventet billettsalg",
        },
    },
};

// Predicts the rest of the ticket sale by extrapolating the sales rate of each group linearly.
// The sales rate is measured over the whole ticket sale, optionally leaving out the first day or only looking at the last week
const SalesPrediction = ({ event, tickets, mappings }) => {
    const [ignoreFirstDay, setIgnoreFirstDay] = useState(true);
    const [lastWeekOnly, setLastWeekOnly] = useState(false);

    const { notice, chart } = useMemo(() => {
        if (!event || Date.now() / 1000 < event.booking_time) {
            return { notice: null, chart: null };
        }

        const groups = Object.entries(event.ticket_sales_caps ?? {}).map(([group, cap]) => {
            const groupTicketTypes = mappings
                .filter((mapping) => mapping.sales_cap_groups.includes(group))
                .map((mapping) => mapping.ticket_type.uuid);

            return {
                group,
                cap,
                created: tickets
                    .filter((ticket) => groupTicketTypes.includes(ticket.ticket_type.uuid))
                    .map((ticket) => ticket.created),
            };
        });

        if (!groups.length) {
            return {
                notice: { type: "info", title: "Kan ikke forutsi billettsalget", detail: "Arrangementet har ingen salgsgrenser å sammenligne salget med" },
                chart: null
            };
        }

        // Calendar days of the ticket sale, as the timestamp each day ends at
        const dayEnds = [];
        const date = new Date(event.booking_time * 1000);
        date.setHours(0, 0, 0, 0);
        do {
            date.setDate(date.getDate() + 1);
            dayEnds.push(date.getTime() / 1000);
        } while (date.getTime() / 1000 < event.start_time);

        const now = Math.min(Date.now() / 1000, event.start_time);

        let rateStart = event.booking_time;
        if (ignoreFirstDay) {
            rateStart = Math.max(rateStart, dayEnds[0]);
        }
        if (lastWeekOnly) {
            rateStart = Math.max(rateStart, now - 7 * SECONDS_PER_DAY);
        }

        // rate is tickets per second, or null if there is no time to measure it over
        const predictions = groups.map((group) => ({
            ...group,
            sold: group.created.length,
            rate: now > rateStart ? group.created.filter((created) => created >= rateStart).length / (now - rateStart) : null,
        }));

        let notice;
        if (predictions.every((group) => group.sold >= group.cap)) {
            notice = { type: "success", title: "Arrangementet er utsolgt" };
        } else if (predictions.some((group) => group.rate === null)) {
            notice = { type: "info", title: "Kan ikke forutsi billettsalget", detail: "Det finnes ikke nok salgsdata med valgte innstillinger" };
        } else {
            // The event is sold out when every group is, so it sells out when the last group does
            const soldOutTimes = predictions.map((group) => {
                if (group.sold >= group.cap) {
                    return now;
                }
                return group.rate > 0 ? now + (group.cap - group.sold) / group.rate : Infinity;
            });
            const soldOutTime = Math.max(...soldOutTimes);

            if (soldOutTime <= event.start_time) {
                notice = {
                    type: "success",
                    title: "Arrangementet ligger an til å bli utsolgt",
                    detail: `Forventet utsolgt ${TimestampToDateTime(soldOutTime, "DD_MM_YYYY_HH_MM")}`
                };
            } else {
                const remainingByGroup = predictions.map((group) => ({
                    group: group.group,
                    remaining: Math.max(Math.round(group.cap - group.sold - group.rate * (event.start_time - now)), 0),
                }));
                const remaining = remainingByGroup.reduce((sum, entry) => sum + entry.remaining, 0);
                notice = {
                    type: "error",
                    title: "Arrangementet ligger ikke an til å bli utsolgt",
                    detail: remainingByGroup.length > 1
                        ? `Forventet ${remaining} ledige billetter ved arrangementstart (${remainingByGroup.map((entry) => `${entry.group}: ${entry.remaining}`).join(", ")})`
                        : `Forventet ${remaining} ledige billetter ved arrangementstart`
                };
            }
        }

        // Each group gets a line for the tickets sold so far, the predicted sales from today and its cap
        const todayIndex = dayEnds.findIndex((dayEnd) => dayEnd >= now);
        const chart = {
            labels: dayEnds.map((dayEnd) => TimestampToDateTime(dayEnd - 1, "DD_MM_YYYY")),
            datasets: predictions.flatMap((group, index) => {
                const color = PREDICTION_COLORS[index % PREDICTION_COLORS.length];

                return [
                    {
                        label: `${group.group} solgt`,
                        borderColor: color,
                        backgroundColor: color,
                        data: dayEnds.map((dayEnd, dayIndex) => dayIndex > todayIndex ? null : group.created.filter((created) => created < dayEnd).length),
                    },
                    ...(group.rate === null ? [] : [{
                        label: `${group.group} forventet`,
                        borderColor: color,
                        backgroundColor: color,
                        borderDash: [6, 4],
                        data: dayEnds.map((dayEnd, dayIndex) => {
                            if (dayIndex < todayIndex) {
                                return null;
                            }
                            if (dayIndex === todayIndex) {
                                return group.sold;
                            }
                            const predicted = Math.round(group.sold + group.rate * (Math.min(dayEnd, event.start_time) - now));
                            return Math.max(Math.min(predicted, group.cap), group.sold);
                        }),
                    }]),
                    {
                        label: `${group.group} grense`,
                        borderColor: color,
                        backgroundColor: color,
                        borderWidth: 1,
                        borderDash: [2, 4],
                        data: dayEnds.map(() => group.cap),
                    },
                ];
            }),
        };

        return { notice, chart };
    }, [event, tickets, mappings, ignoreFirstDay, lastWeekOnly]);

    return (
        <InnerContainer flex="1" floattop visible={!!notice}>
            <InnerContainerTitle>Salgsprognose</InnerContainerTitle>
            <InnerContainer floattop rowgap>
                <InnerContainerRow>
                    <InputCheckbox label="Ignorer første dag" value={ignoreFirstDay} onChange={() => setIgnoreFirstDay(!ignoreFirstDay)} />
                    <InputCheckbox label="Bare siste uken" value={lastWeekOnly} onChange={() => setLastWeekOnly(!lastWeekOnly)} />
                </InnerContainerRow>
                <Notice large fillWidth type={notice?.type} visible={!!notice}>
                    <InnerContainerTitleS nopadding={!notice?.detail}>{notice?.title}</InnerContainerTitleS>
                    {notice?.detail}
                </Notice>
                {chart && <Line options={predictionChartOptions} data={chart} />}
            </InnerContainer>
        </InnerContainer>
    );
};

export const TicketSalesStatus = () => {
    const { brandUuid } = useBrand();

    const authContext = useContext(AuthenticationContext);

    const viewTickets = hasAnyBrandPermission(authContext.roles, brandUuid, ["ticket_admin"]);

    const { data: currentEvent, isLoading: isLoadingCurrentEvent } = useCurrentEvent(brandUuid);
    const { data: tickets = [], isLoading: isLoadingTickets } = useEventTickets(viewTickets ? currentEvent?.uuid : undefined);
    const { data: storeSessions = [], isLoading: isLoadingStoreSessions } = useActiveStoreSessions(viewTickets ? currentEvent?.uuid : undefined);
    const { data: mappings = [], isLoading: isLoadingMappings } = useEventTicketTypeMappings(viewTickets ? currentEvent?.uuid : undefined);
    const { data: salesData, isLoading: isLoadingSalesData } = useTicketSaleData(viewTickets ? brandUuid : undefined, true);

    const loading = viewTickets && (
        isLoadingCurrentEvent ||
        (currentEvent && (isLoadingTickets || isLoadingStoreSessions || isLoadingMappings || isLoadingSalesData))
    );

    const { groupBars, ticketTypeBars } = useMemo(() => {
        if (!currentEvent) {
            return { groupBars: [], ticketTypeBars: [] };
        }

        // Count tickets held in store sessions for this event, per ticket type
        const heldTickets = {};
        storeSessions.forEach((storeSession) => {
            storeSession.entries.forEach((entry) => {
                heldTickets[entry.ticket_type.uuid] = (heldTickets[entry.ticket_type.uuid] ?? 0) + entry.amount;
            })
        })

        const ticketsByType = {};
        tickets.forEach((ticket) => {
            ticketsByType[ticket.ticket_type.uuid] = [...(ticketsByType[ticket.ticket_type.uuid] ?? []), ticket];
        })

        const ticketSalesCaps = currentEvent.ticket_sales_caps ?? {};
        const sortedMappings = [...mappings].sort((a, b) => b.ticket_type.price - a.ticket_type.price);

        // Creates a sales bar for the tickets of the given mappings. cap is null if the tickets are unlimited
        const createBar = (key, title, barMappings, cap) => {
            const barTickets = barMappings.flatMap((mapping) => ticketsByType[mapping.ticket_type.uuid] ?? []);
            const held = barMappings.reduce((count, mapping) => count + (heldTickets[mapping.ticket_type.uuid] ?? 0), 0);

            return {
                key,
                title,
                sold: barTickets.length,
                cap,
                soldLegend: [
                    { color: "green", text: cap === null ? `${barTickets.length} billetter solgt` : `${barTickets.length} / ${cap} billetter solgt` },
                    { color: "stripedOrange", text: `${held} billetter reservert i aktive kjøp` },
                ],
                soldSegments: [
                    ...barMappings.map((mapping) => ({
                        key: mapping.uuid,
                        color: mapping.ticket_type.price ? "green" : "stripedGreen",
                        title: mapping.ticket_type.name,
                        count: (ticketsByType[mapping.ticket_type.uuid] ?? []).length
                    })),
                    {
                        key: "reservedTickets",
                        color: "stripedOrange",
                        title: "Billetter reservert i kjøp",
                        count: held
                    },
                    {
                        key: "availableTickets",
                        color: "gray",
                        title: "Tilgjengelige billetter",
                        count: cap === null ? 0 : Math.max(cap - barTickets.length - held, 0),
                        fillOnEmpty: !barTickets.length && !held
                    },
                ],
            };
        }

        // Groups also get a checkin bar, counting the tickets in the group which can be checked in
        const groupBars = Object.entries(ticketSalesCaps)
            .map(([group, cap]) => {
                const groupMappings = sortedMappings.filter((mapping) => mapping.sales_cap_groups.includes(group));
                const groupTickets = groupMappings
                    .filter((mapping) => mapping.ticket_type.grants_admission)
                    .flatMap((mapping) => ticketsByType[mapping.ticket_type.uuid] ?? []);
                const checkedIn = groupTickets.filter((ticket) => ticket.checked_in).length;

                return {
                    ...createBar(group, group, groupMappings, cap),
                    checkinLegend: [
                        { color: "green", text: `${checkedIn} / ${groupTickets.length} billetter sjekket inn` },
                    ],
                    checkinSegments: [
                        {
                            key: "checkedinTickets",
                            color: "green",
                            title: "Billetter sjekket inn",
                            count: checkedIn
                        },
                        {
                            key: "notCheckedinTickets",
                            color: "gray",
                            title: "Billetter ikke sjekket inn",
                            count: groupTickets.length - checkedIn,
                            fillOnEmpty: !checkedIn
                        },
                    ],
                };
            });

        // A ticket type is limited by its own sales cap and by the caps of its groups
        const ticketTypeBars = sortedMappings.map((mapping) => {
            const caps = [mapping.sales_cap, ...mapping.sales_cap_groups.map((group) => ticketSalesCaps[group])].filter((cap) => cap !== null && cap !== undefined);
            return createBar(mapping.uuid, mapping.ticket_type.name, [mapping], caps.length ? Math.min(...caps) : null);
        });

        return { groupBars, ticketTypeBars };
    }, [currentEvent, tickets, storeSessions, mappings]);

    const salesNotStarted = !!currentEvent && Date.now() / 1000 < currentEvent.booking_time;

    // Compares the tickets sold so far with previous events at the same day of their ticket sale.
    // Days are calendar days, where the day the ticket sale starts is day 1
    const comparison = useMemo(() => {
        if (!currentEvent || salesNotStarted) {
            return null;
        }

        const previousSales = (salesData ?? []).filter((salesEvent) => salesEvent.event.uuid !== currentEvent.uuid);
        if (!previousSales.length) {
            return { type: "info", title: "Informasjon om hvor bra billettsalget går vil komme her når det finnes historisk data" };
        }

        const bookingDate = new Date(currentEvent.booking_time * 1000);
        bookingDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const day = Math.round((today - bookingDate) / (SECONDS_PER_DAY * 1000)) + 1;
        const currentSales = (salesData ?? []).find((salesEvent) => salesEvent.event.uuid === currentEvent.uuid);
        const currentSold = currentSales ? getSoldByDay(currentSales, day) : 0;
        const averageSold = previousSales.reduce((sum, salesEvent) => sum + getSoldByDay(salesEvent, day), 0) / previousSales.length;

        if (averageSold <= 0) {
            return { type: "info", title: `Tidligere arrangementer hadde ingen salg innen dag ${day} i billettsalget` };
        }

        const better = currentSold >= averageSold;
        return {
            type: better ? "success" : "warning",
            title: `${Math.round(Math.abs(currentSold - averageSold) / averageSold * 100)}% ${better ? "bedre" : "dårligere"} enn gjennomsnittet`,
            detail: `${currentSold} billetter solgt mot et snitt på ${Math.round(averageSold)} for dag ${day} i billettsalget`
        };
    }, [currentEvent, salesNotStarted, salesData]);

    if(loading) {
        return (
            <PageLoading />
        )
    }

    return (
        <>
            <DashboardHeader border>
                <DashboardTitle>
                    Billettsalg-status
                </DashboardTitle>
            </DashboardHeader>
            <DashboardContent>
                <InnerContainer visible={!viewTickets}>
                    <InnerContainerRow>
                        <Notice type="error" visible={!viewTickets}>
                            Du har ikke tilgang til å se billetter.
                        </Notice>
                    </InnerContainerRow>
                </InnerContainer>

                <InnerContainer visible={viewTickets && !currentEvent}>
                    <InnerContainerRow>
                        <Notice fillWidth type="warning" visible={viewTickets && !currentEvent}>
                            Det eksisterer for øyeblikket ingen aktive arrangementer.<br/>
                        </Notice>
                    </InnerContainerRow>
                </InnerContainer>

                <InnerContainer visible={viewTickets && salesNotStarted}>
                    <InnerContainerRow>
                        <Notice large fillWidth type="info" visible={salesNotStarted}>
                            <InnerContainerTitleS nopadding>Billettsalget har ikke startet enda</InnerContainerTitleS>
                        </Notice>
                    </InnerContainerRow>
                </InnerContainer>

                <InnerContainer visible={viewTickets && !!comparison}>
                    <InnerContainerRow>
                        <SalesPrediction event={currentEvent} tickets={tickets} mappings={mappings} />
                    </InnerContainerRow>
                </InnerContainer>

                <InnerContainer visible={viewTickets && !!comparison}>
                    <InnerContainerRow>
                        <Notice large fillWidth type={comparison?.type} visible={!!comparison}>
                            <InnerContainerTitleS nopadding={!comparison?.detail}>{comparison?.title}</InnerContainerTitleS>
                            {comparison?.detail}
                        </Notice>
                    </InnerContainerRow>
                </InnerContainer>

                <InnerContainer rowgap visible={viewTickets && !!currentEvent}>
                    <InnerContainerRow>
                        <InnerContainer flex="1" floattop>
                            <InnerContainer>
                                <InnerContainerTitle>Billettfordeling</InnerContainerTitle>
                                <InnerContainer column extramargin border>
                                    <InnerContainerTitleS>Per gruppe</InnerContainerTitleS>
                                    {groupBars.map((bar) => (
                                        <FlexBar title={bar.title} legend={bar.soldLegend} key={bar.key}>
                                            {bar.soldSegments.map((object) => {
                                                return (<BarElement color={object.color} title={object.title} count={object.count} fillOnEmpty={object.fillOnEmpty} key={object.key} />)
                                            })}
                                        </FlexBar>
                                    ))}
                                </InnerContainer>
                                <InnerContainer column extramargin>
                                    <InnerContainerTitleS>Per billettype</InnerContainerTitleS>
                                    {ticketTypeBars.map((bar) => (
                                        <FlexBar title={bar.title} legend={bar.soldLegend} key={bar.key}>
                                            {bar.soldSegments.map((object) => {
                                                return (<BarElement color={object.color} title={object.title} count={object.count} fillOnEmpty={object.fillOnEmpty} key={object.key} />)
                                            })}
                                        </FlexBar>
                                    ))}
                                </InnerContainer>
                            </InnerContainer>
                        </InnerContainer>

                        <InnerContainer flex="1" floattop>
                            <InnerContainer>
                                <InnerContainerTitle>Innsjekkede billetter</InnerContainerTitle>
                                <InnerContainer column extramargin>
                                    <InnerContainerTitleS>Per gruppe</InnerContainerTitleS>
                                    {groupBars.map((bar) => (
                                        <FlexBar title={bar.title} legend={bar.checkinLegend} key={bar.key}>
                                            {bar.checkinSegments.map((object) => {
                                                return (<BarElement color={object.color} title={object.title} count={object.count} fillOnEmpty={object.fillOnEmpty} key={object.key} />)
                                            })}
                                        </FlexBar>
                                    ))}
                                </InnerContainer>
                            </InnerContainer>
                        </InnerContainer>
                    </InnerContainerRow>
                </InnerContainer>
            </DashboardContent>
        </>
    )
}
